/**
 * The shift note panel. Opens when your own Dialpad status changes into off duty while the desk is
 * open (once per change, never on later messages while you stay off), or from "Hand off" in the
 * rail. One optional line per open ticket you hold, for whoever picks it up; each filled line is
 * saved as a shift note in that ticket's history. A line that fails says why and stays; lines
 * already saved are not sent again.
 */
import { type TeamMember } from '@lsc/contract';
import { type Holder } from '@lsc/domain';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { closeShiftNote, openShiftNote, useShiftNoteOpen } from './shift-store';

export interface ShiftTicket {
  ticketId: string;
  ticketNumber: string;
  subject: string;
  holders: Holder[];
}

interface Props {
  team: TeamMember[] | null;
  viewerEmail: string;
  tickets: ShiftTicket[];
  save: (ticketId: string, text: string) => Promise<void>;
  /** The board's tickets are still loading. */
  loading?: boolean;
  /** The board's tickets did not load, in words. */
  loadError?: string | null;
}

const MAX = 200;

type LineState = { kind: 'saved' } | { kind: 'error'; message: string };

export function ShiftNote({ team, viewerEmail, tickets, save, loading = false, loadError = null }: Props) {
  const open = useShiftNoteOpen();
  const email = viewerEmail.toLowerCase();
  const onDuty = team?.find((m) => m.email === email)?.onDuty;
  const last = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    // Only a change from on duty to off duty opens it; the first status seen never does.
    if (last.current === true && onDuty === false) openShiftNote();
    if (onDuty !== undefined) last.current = onDuty;
  }, [onDuty]);

  if (!open) return null;
  return <Panel tickets={tickets.filter((t) => t.holders.some((h) => h.email === email))} save={save} loading={loading} loadError={loadError} />;
}

function Panel({ tickets, save, loading, loadError }: { tickets: ShiftTicket[]; save: Props['save']; loading: boolean; loadError: string | null }) {
  const [lines, setLines] = useState<Record<string, string>>({});
  const [state, setState] = useState<Record<string, LineState>>({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    const next: Record<string, LineState> = { ...state };
    for (const t of tickets) {
      const text = (lines[t.ticketId] ?? '').trim();
      if (text === '' || next[t.ticketId]?.kind === 'saved') continue;
      try {
        await save(t.ticketId, text);
        next[t.ticketId] = { kind: 'saved' };
      } catch (err) {
        next[t.ticketId] = { kind: 'error', message: err instanceof Error ? err.message : 'That note did not save.' };
      }
    }
    setState(next);
    setSaving(false);
    if (!Object.values(next).some((s) => s.kind === 'error')) closeShiftNote();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-canvas/70 pt-[10vh]">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="shift-title"
        onKeyDown={(e) => {
          if (e.key === 'Escape') closeShiftNote();
        }}
        className="flex max-h-[80vh] w-full max-w-[560px] flex-col overflow-hidden rounded-panel border border-line bg-surface shadow-xl"
      >
        <div className="border-b border-line px-5 py-3">
          <h2 id="shift-title" className="text-title font-medium">
            Going off duty
          </h2>
          <p className="mt-0.5 text-hint text-ink-3">Leave a note for whoever picks these up. Skip any you like.</p>
        </div>
        <div className="flex flex-col gap-3 overflow-y-auto px-5 py-4">
          {loadError ? (
            <p role="alert" className="text-body text-alert">
              {loadError}
            </p>
          ) : loading ? (
            <p className="text-body text-ink-3">Finding your open tickets</p>
          ) : tickets.length === 0 ? (
            <p className="text-body text-ink-3">You are not holding any open tickets.</p>
          ) : (
            tickets.map((t) => {
              const s = state[t.ticketId];
              const label = `Note for ${t.ticketNumber}, ${t.subject}`;
              return (
                <div key={t.ticketId}>
                  <p className="text-body text-ink">
                    {t.subject} <span className="mono text-hint text-ink-3">{t.ticketNumber}</span>
                  </p>
                  {s?.kind === 'saved' ? (
                    <p className="mt-1 text-hint text-live">Saved</p>
                  ) : (
                    <input
                      aria-label={label}
                      maxLength={MAX}
                      value={lines[t.ticketId] ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLines((l) => ({ ...l, [t.ticketId]: v }));
                      }}
                      placeholder="Where it stands, what is next"
                      className="mt-1 h-8 w-full rounded-control border border-line bg-canvas px-2.5 text-body text-ink placeholder:text-ink-3 focus:border-info focus-visible:outline-none"
                    />
                  )}
                  {s?.kind === 'error' && (
                    <p role="alert" className="mt-1 text-hint text-alert">
                      {s.message}
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
          <Button onClick={closeShiftNote}>Skip</Button>
          {tickets.length > 0 && (
            <Button
              variant="primary"
              disabled={saving}
              onClick={() => {
                void submit();
              }}
            >
              Save
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
